import { useMutation, useQueryClient } from '@tanstack/react-query';

import { connectorService } from '@/services/connector-service';
import { pollForResource } from '@/utils/poll';
import type { UUID } from '@/utils/uuid';

import { connectorMutationKeys, connectorQueryKeys } from './query-keys';

/**
 * Hook to create a connector.
 * Returns a react-query mutation that accepts connector creation payload.
 * Invalidates the connectors cache to trigger a refetch.
 */
export function useCreateConnector(tenantId?: UUID) {
  const qc = useQueryClient();

  return useMutation({
    mutationKey: connectorMutationKeys.createConnector(tenantId),
    mutationFn: async (payload: {
      name: string;
      description?: string;
      providerId: UUID;
      requirements?: Array<string>;
    }) => {
      if (!tenantId) throw new Error('Missing tenant id');

      const connector = await connectorService.createConnector(
        tenantId,
        payload
      );

      const projected = await pollForResource(() =>
        connectorService.findConnector(tenantId, connector.id)
      );

      return projected ?? connector;
    },
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: connectorQueryKeys.listByTenant(tenantId),
        refetchType: 'all',
      });
    },
  });
}
